import { useQuery } from '@tanstack/react-query';
import { ApiService } from './api';

export const DEFAULT_THEME = {
  primaryColor: '#1f4b43',
  accentColor: '#e07a1f',
  backgroundColor: '#fbf6e9',
  headerColor: '#1f4b43',
};

/** CSS variables the premium pages read (bg-[var(--tk-primary)] etc.). */
export const themeStyle = (theme) => {
  const t = { ...DEFAULT_THEME, ...Object.fromEntries(Object.entries(theme || {}).filter(([, v]) => v)) };
  return {
    '--tk-primary': t.primaryColor,
    '--tk-accent': t.accentColor,
    '--tk-bg': t.backgroundColor,
    '--tk-header': t.headerColor,
    backgroundColor: t.backgroundColor,
  };
};

/** Public premium-registration view; `ticketing` is null when the event uses the regular form. */
export const useTicketing = (eventRef, passId) => useQuery({
  queryKey: ['ticketing', eventRef, passId || null],
  queryFn: () => ApiService.getTicketing(eventRef, passId),
  enabled: !!eventRef,
  staleTime: 60 * 1000,
});

/** Keeps an invite code, and the on-spot desk flag, across the premium pages. */
export const inviteSearch = (search) => {
  const params = new URLSearchParams(search || '');
  const kept = new URLSearchParams();
  const invite = params.get('invite');
  if (invite) kept.set('invite', invite);
  if (params.get('onspot') === '1' || (typeof window !== 'undefined' && window.location.pathname.endsWith('/on-spot'))) {
    kept.set('onspot', '1');
  }
  const text = kept.toString();
  return text ? `?${text}` : '';
};

export const premiumHome = (eventRef, search) => (
  new URLSearchParams(search || '').get('onspot') === '1'
    ? `/register/${eventRef}/on-spot?enableCta=true`
    : `/register/${eventRef}${inviteSearch(search)}`
);

export const isVideo = (url) => /\.(mp4|webm|mov)(\?|$)/i.test(url || '');

export const passCurrency = (pass, view) => (pass?.currency || view?.currency || 'INR').toUpperCase();

export const formatMoney = (amount, currency = 'INR') => {
  const value = Number(amount) || 0;
  try {
    return new Intl.NumberFormat(currency === 'INR' ? 'en-IN' : 'en-US', {
      style: 'currency', currency, minimumFractionDigits: 2, maximumFractionDigits: 2,
    }).format(value);
  } catch (_) {
    return `${currency} ${value.toFixed(2)}`;
  }
};

export const TYPE_LABEL = { FREE: 'Free', PAID: 'Paid', APPROVAL: 'Approval based' };

/** open | upcoming | closed, from the pass sales window (server times are UTC without a zone). */
export const passSaleState = (pass, now = Date.now()) => {
  const parse = (value) => (value ? Date.parse(/Z|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`) : null);
  const start = parse(pass?.salesStartAt);
  const end = parse(pass?.salesEndAt);
  if (start && now < start) return 'upcoming';
  if (end && now > end) return 'closed';
  return 'open';
};

const CART_KEY = (eventId) => `tkCart:${eventId}`;

/** One pass type per booking, as on the pass picker: { passId, qty }. */
export const readCart = (eventId) => {
  try {
    const cart = JSON.parse(sessionStorage.getItem(CART_KEY(eventId)) || 'null');
    return cart?.passId ? { passId: cart.passId, qty: Math.max(1, Number(cart.qty) || 1) } : null;
  } catch (_) {
    return null;
  }
};

export const writeCart = (eventId, cart) => {
  try {
    if (cart) sessionStorage.setItem(CART_KEY(eventId), JSON.stringify(cart));
    else sessionStorage.removeItem(CART_KEY(eventId));
  } catch (_) { /* storage unavailable */ }
};

export const formatEventDate = (value) => {
  if (!value) return null;
  const date = new Date(/Z|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`);
  if (Number.isNaN(date.getTime())) return null;
  return {
    date: date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kolkata' }),
    time: date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }).toUpperCase(),
  };
};

export const paragraphs = (text) => String(text || '').split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);
